import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { styled } from 'styled-components';
import { useForm } from 'react-hook-form';
import { MdWarning } from 'react-icons/md';

import { ONLY_NUMBERS_GREATER_THAN_ZERO, TWO_DECIMALS_GREATER_THAN_ZERO, TWO_DECIMALS_ZERO_AND_POSITIVE } from '../../constants/regex';
import { ModalButton } from '../../styles/StyledComponents';
import { formatCurrency } from '../../utils/numbers';

const ReceiptEditDetailModal = ({ show, detail, closeHandler, actionHandler }) => {
    // ----- State
    let [regexAmount, setRegexAmount] = useState(null);
    let [showDiscountError, setShowDiscountError] = useState(null);
    let [showStockWarning, setShowStockWarning] = useState(false);
    let [confirmationChecked, setConfirmationChecked] = useState(false);

    // ----- Context/Hooks
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            amount: '',
            itemsValue: '',
            discount: '',
            extraCharges: '',
            tax: '',
        },
    });

    // Watch the itemsValue field to trigger validation
    const itemsValue = watch('itemsValue');

    useEffect(() => {
        reset(detail);

        if (detail.measureUnit === 'UNITS') {
            setRegexAmount(ONLY_NUMBERS_GREATER_THAN_ZERO);
        } else {
            setRegexAmount(TWO_DECIMALS_GREATER_THAN_ZERO);
        }
    }, [detail, reset]);

    // Check stock when itemsValue changes
    useEffect(() => {
        // Only check if it's an article (not a service), has average value, and itemsValue is valid
        if (!detail.isService && detail.averageStockValue && itemsValue && itemsValue !== '' && !isNaN(itemsValue)) {
            const threshold = detail.averageStockValue * 1.2;

            // Check if the new item value exceeds the average by more than 20%
            if (Number(itemsValue) > threshold) {
                setShowStockWarning(true);
                setConfirmationChecked(false);
            } else {
                setShowStockWarning(false);
                setConfirmationChecked(false);
            }
        } else {
            setShowStockWarning(false);
        }
    }, [itemsValue, detail.isService, detail.averageStockValue]);

    // ----- Actions
    const handleClose = () => {
        closeHandler();
    };

    const handleAction = (data) => {
        if (checkDiscount(data)) {
            detail.amount = Number(data.amount);
            detail.itemsValue = Number(data.itemsValue);
            detail.discount = Number(data.discount);
            detail.extraCharges = Number(data.extraCharges);
            detail.tax = Number(data.tax);

            actionHandler(detail);
        }
    };

    const checkDiscount = (data) => {
        let allGood = true;
        setShowDiscountError(false);

        if (data.discount !== '') {
            if (Number(data.itemsValue * data.amount) < Number(data.discount)) {
                allGood = false;
                setShowDiscountError(true);
            }
        }
        return allGood;
    };

    const handleCheckboxChange = (e) => {
        setConfirmationChecked(e.target.checked);
    };

    // ----- Render
    return (
        <Modal show={show} backdrop='static' centered onHide={handleClose}>
            <Modal.Header className='fw-bold' closeButton>
                Editar detalle
            </Modal.Header>
            <Modal.Body>
                <FormStyled onSubmit={handleSubmit(handleAction)}>
                    <p className='fw-bold mb-0 text-center'>
                        {detail.isService ? `${detail.serviceConcept}` : `${detail.articleName} (${detail.brand})`}
                    </p>

                    <hr />

                    <label className='d-flex mb-1' htmlFor='amount'>
                        Cantidad
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='amount'
                        autoComplete='off'
                        maxLength={10}
                        {...register('amount', { required: true, maxLength: 10, pattern: regexAmount })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.amount?.type === 'required' && `Campo requerido`}
                        {errors.amount?.type === 'maxLength' && `Formato inválido`}
                        {errors.amount?.type === 'pattern' && detail.measureUnit !== 'UNITS' && `Sólo se permiten números (5 - 3.50)`}
                        {errors.amount?.type === 'pattern' && detail.measureUnit === 'UNITS' && `Sólo se permiten números enteros (5 - 100)`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='itemsValue'>
                        Valor por unidad
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='itemsValue'
                        autoComplete='off'
                        maxLength={10}
                        {...register('itemsValue', { required: true, maxLength: 10, pattern: TWO_DECIMALS_GREATER_THAN_ZERO })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.itemsValue?.type === 'required' && `Campo requerido`}
                        {errors.itemsValue?.type === 'maxLength' && `Formato inválido`}
                        {errors.itemsValue?.type === 'pattern' && `Sólo se permiten números (5 - 3.50)`}
                    </span>

                    {showStockWarning && (
                        <WarningContainer>
                            <p className='warning-text'>
                                <MdWarning className='warning-icon' />
                                El valor por unidad para este artículo sobrepasa en más del 20% del último valor promedio en inventario:{' '}
                                {`₡${formatCurrency(detail.averageStockValue)}`}
                            </p>
                            <div className='checkbox-container'>
                                <input type='checkbox' id='confirmWarning' checked={confirmationChecked} onChange={handleCheckboxChange} />
                                <label htmlFor='confirmWarning' className='checkbox-label'>
                                    Deseo continuar con este valor
                                </label>
                            </div>
                        </WarningContainer>
                    )}

                    <label className='d-flex mb-1' htmlFor='discount'>
                        Descuento
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='discount'
                        autoComplete='off'
                        maxLength={10}
                        {...register('discount', { maxLength: 10, pattern: TWO_DECIMALS_ZERO_AND_POSITIVE })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.discount?.type === 'maxLength' && `Formato inválido`}
                        {errors.discount?.type === 'pattern' && `Sólo se permiten números (5 - 3.50)`}
                        {showDiscountError && `Debe ser menor o igual al valor total del detalle (Cantidad X Valor por unidad)`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='extraCharges'>
                        Otros cargos
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='extraCharges'
                        autoComplete='off'
                        maxLength={10}
                        {...register('extraCharges', { maxLength: 10, pattern: TWO_DECIMALS_ZERO_AND_POSITIVE })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.extraCharges?.type === 'maxLength' && `Formato inválido`}
                        {errors.extraCharges?.type === 'pattern' && `Sólo se permiten números (5 - 3.50)`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='tax'>
                        Impuesto a aplicar (IVA %)
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='tax'
                        autoComplete='off'
                        maxLength={10}
                        {...register('tax', { required: true, maxLength: 10, pattern: /(^100([.]0{1,2})?)$|(^\d{1,2}([.]\d{1,2})?)$/ })}
                    />
                    <span className='my-1' role='alert'>
                        {errors.tax?.type === 'required' && `Campo requerido`}
                        {errors.tax?.type === 'maxLength' && `Formato inválido`}
                        {errors.tax?.type === 'pattern' && `Sólo se permiten números entre 0 y 100`}
                    </span>

                    <div className='mt-3 d-flex justify-content-end gap-2'>
                        <ModalButton className='cancel' onClick={handleClose}>
                            Cancelar
                        </ModalButton>
                        <ModalButton className='action' type='submit' disabled={showStockWarning && !confirmationChecked}>
                            Editar
                        </ModalButton>
                    </div>
                </FormStyled>
            </Modal.Body>
        </Modal>
    );
};

const FormStyled = styled.form`
    display: grid;

    > label {
        font-size: 13px;
        font-weight: bold;
    }

    > span {
        color: #d04437;
        font-size: 12px;
        min-height: 12px;
    }

    > button {
        background-color: #d04437;
    }
`;

const WarningContainer = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 10px;

    .warning-text {
        color: #856404;
        font-size: 13px;
        font-weight: 500;
        margin: 0 0 10px 0;
        display: flex;
        align-items: center;
        gap: 6px;

        .warning-icon {
            font-size: 18px;
            flex-shrink: 0;
            color: #f26733;
        }
    }

    .checkbox-container {
        display: flex;
        align-items: center;
        gap: 8px;

        input[type='checkbox'] {
            cursor: pointer;
            width: 16px;
            height: 16px;
        }

        .checkbox-label {
            cursor: pointer;
            font-size: 13px;
            margin: 0;
            user-select: none;
        }
    }
`;

export default ReceiptEditDetailModal;
